// src/services/lazyCardDefinitions.js
import React, { lazy } from 'react';
import { cardRegistry } from './cardRegistry';
import LazyCard from '../components/dashboard/base/LazyCard';
import { dashboardCardDefinitions } from '../config/dashboardCardDefinitions';
import UniversalDashboardCard from '../components/dashboard/cards/universal/UniversalDashboardCard';
import userCardService from './userCardService';

// Lazy load all card components
const TopCategoriesCard = lazy(() => import('../components/dashboard/cards/sales/TopCategoriesCard'));
const BestSoldProductsCard = lazy(() => import('../components/dashboard/cards/sales/BestSoldProductsCard'));
const TrendingProductsCard = lazy(() => import('../components/dashboard/cards/sales/TrendingProductsCard'));
const SalesByRepCard = lazy(() => import('../components/dashboard/cards/sales/SalesByRepCard'));
const SalesRepCustomersCard = lazy(() => import('../components/dashboard/cards/sales/SalesRepCustomersCard'));
const SalesRepProductsCard = lazy(() => import('../components/dashboard/cards/sales/SalesRepProductsCard'));
const SalesForecastCard = lazy(() => import('../components/dashboard/cards/sales/SalesForecastCard'));
const SalesTargetsCard = lazy(() => import('../components/dashboard/cards/sales/SalesTargetsCard'));
const AllSalesTargetsCard = lazy(() => import('../components/dashboard/cards/sales/AllSalesTargetsCard'));
const TopCustomersCard = lazy(() => import('../components/dashboard/cards/customer/TopCustomersCard'));
const CustomerAnalyticsCard = lazy(() => import('../components/dashboard/cards/customers/CustomerAnalyticsCard'));
const BusinessSummaryCard = lazy(() => import('../components/dashboard/cards/analytics/BusinessSummaryCard'));
const BreakEvenCard = lazy(() => import('../components/dashboard/cards/analytics/BreakEvenCard'));
const ProfitLossCard = lazy(() => import('../components/dashboard/cards/analytics/ProfitLossCard'));
const CashFlowCard = lazy(() => import('../components/dashboard/cards/analytics/CashFlowCard'));
const FinancialPositionCard = lazy(() => import('../components/dashboard/cards/analytics/FinancialPositionCard'));
const OnlineUsersCard = lazy(() => import('../components/dashboard/cards/user/OnlineUsersCard'));
const SalesOverviewCard = lazy(() => import('../components/dashboard/cards/sales/SalesOverviewCard'));
const CashManagementSummaryCard = lazy(() => import('../components/dashboard/cards/cashmanagement/CashManagementSummaryCard'));
const CashFlowForecastCard = lazy(() => import('../components/dashboard/cards/cashmanagement/CashFlowForecastCard'));
const ReceivablesCard = lazy(() => import('../components/dashboard/cards/cashmanagement/ReceivablesCard'));
const PayablesCard = lazy(() => import('../components/dashboard/cards/cashmanagement/PayablesCard'));
const StockValuationCard = lazy(() => import('../components/dashboard/cards/inventory/StockValuationCard'));
const PurchasesBySupplierCard = lazy(() => import('../components/dashboard/cards/purchases/PurchasesBySupplierCard'));
const CustomerAgeingCard = lazy(() => import('../components/dashboard/cards/cashmanagement/CustomerAgeingCard'));

/**
 * Register cards with lazy loading based on user permissions
 * @param {Array} permittedCardIds - Array of card IDs the user has permission to view
 */
export function registerCardsLazy(permittedCardIds = []) {
  if (cardRegistry.isInitialized() && cardRegistry.getAllCards().length > 0) {
    return;
  }

  // Define all available cards
  const cardDefinitions = [
    {
      id: "online-users",
      definition: {
        title: "Online Users",
        component: (props) => (
          <OnlineUsersCard {...props} />
        ),
        width: 4,
        height: 5,
        responsive: {
          md: { width: 6 },
          sm: { width: 6 },
          xs: { width: 4 }
        }
      },
      options: {
        category: 'users',
        priority: 2
      }
    },
    {
      id: "sales-forecast",
      definition: {
        title: "Sales Forecast",
        component: (props) => (
          <LazyCard component={SalesForecastCard} {...props} />
        ),
        width: 6,
        height: 11,
        responsive: {
          md: { width: 8 },
          sm: { width: 6 },
          xs: { width: 4 }
        }
      },
      options: {
        category: 'sales',
        priority: 2
      }
    },
    {
      id: "business-summary",
      definition: {
        title: "Business Summary",
        component: (props) => (
          <LazyCard component={BusinessSummaryCard} {...props} />
        ),
        width: 4,
        height: 11,
        responsive: {
          md: { width: 6 },
          sm: { width: 6 },
          xs: { width: 4 }
        }
      },
      options: {
        category: 'analytics',
        priority: 1
      }
    },
    {
      id: "break-even",
      definition: {
        title: "Break-even Revenue",
        component: (props) => (
          <LazyCard component={BreakEvenCard} {...props} />
        ),
        width: 4,
        height: 8,
        responsive: {
          md: { width: 6 },
          sm: { width: 6 },
          xs: { width: 4 }
        }
      },
      options: {
        category: 'analytics',
        priority: 2
      }
    },
    {
      id: "profit-loss",
      definition: {
        title: "Profit & Loss",
        component: (props) => (
          <LazyCard component={ProfitLossCard} {...props} />
        ),
        width: 6,
        height: 10,
        responsive: {
          md: { width: 6 },
          sm: { width: 6 },
          xs: { width: 4 }
        }
      },
      options: {
        category: 'analytics',
        priority: 3
      }
    },
    {
      id: "financial-position",
      definition: {
        title: "Financial Position",
        component: (props) => (
          <LazyCard component={FinancialPositionCard} {...props} />
        ),
        width: 4,
        height: 14,
        responsive: {
          md: { width: 6 },
          sm: { width: 6 },
          xs: { width: 4 }
        }
      },
      options: {
        category: 'analytics',
        priority: 4
      }
    },
    {
      id: "cash-flow",
      definition: {
        title: "Cash Flow",
        component: (props) => (
          <LazyCard component={CashFlowCard} {...props} />
        ),
        width: 6,
        height: 12,
        responsive: {
          md: { width: 6 },
          sm: { width: 6 },
          xs: { width: 4 }
        }
      },
      options: {
        category: 'analytics',
        priority: 5
      }
    },
    {
      id: "top-categories",
      definition: {
        title: "Top Categories",
        component: (props) => (
          <TopCategoriesCard {...props} />
        ),
        width: 4,
        height: 5,
        responsive: {
          md: { width: 6 },
          sm: { width: 6 },
          xs: { width: 4 }
        }
      },
      options: {
        category: 'sales',
        priority: 2
      }
    },
    {
      id: "best-sold-products",
      definition: {
        title: "Best Sold Products",
        component: (props) => (
          <BestSoldProductsCard {...props} />
        ),
        width: 4,
        height: 5,
        responsive: {
          md: { width: 6 },
          sm: { width: 6 },
          xs: { width: 4 }
        }
      },
      options: {
        category: 'sales',
        priority: 3
      }
    },
    {
      id: "trending-products",
      definition: {
        title: "Trending Products",
        component: (props) => (
          <TrendingProductsCard {...props} />
        ),
        width: 4,
        height: 5,
        responsive: {
          md: { width: 6 },
          sm: { width: 6 },
          xs: { width: 4 }
        }
      },
      options: {
        category: 'sales',
        priority: 4
      }
    },
    {
      id: "sales-by-rep",
      definition: {
        title: "Sales by Representative",
        component: (props) => (
          <SalesByRepCard {...props} />
        ),
        width: 4,
        height: 5,
        responsive: {
          md: { width: 6 },
          sm: { width: 6 },
          xs: { width: 4 }
        }
      },
      options: {
        category: 'sales',
        priority: 2
      }
    },
    {
      id: "sales-rep-customers",
      definition: {
        title: "Sales Rep Customers",
        component: (props) => (
          <SalesRepCustomersCard {...props} />
        ),
        width: 8,
        height: 10,
        responsive: {
          md: { width: 8 },
          sm: { width: 6 },
          xs: { width: 4 }
        }
      },
      options: {
        category: 'sales',
        priority: 7
      }
    },
    {
      id: "sales-rep-products",
      definition: {
        title: "Sales Rep Products",
        component: (props) => (
          <SalesRepProductsCard {...props} />
        ),
        width: 8,
        height: 10,
        responsive: {
          md: { width: 8 },
          sm: { width: 6 },
          xs: { width: 4 }
        }
      },
      options: {
        category: 'sales',
        priority: 8
      }
    },
    {
      id: "sales-targets",
      definition: {
        title: "My Sales Targets",
        component: (props) => (
          <SalesTargetsCard {...props} />
        ),
        width: 6,
        height: 9,
        responsive: {
          md: { width: 8 },
          sm: { width: 6 },
          xs: { width: 4 }
        }
      },
      options: {
        category: 'sales',
        priority: 9
      }
    },
    {
      id: "all-sales-targets",
      definition: {
        title: "Team Targets",
        component: (props) => (
          <AllSalesTargetsCard {...props} />
        ),
        width: 6,
        height: 11,
        responsive: {
          md: { width: 8 },
          sm: { width: 6 },
          xs: { width: 4 }
        }
      },
      options: {
        category: 'sales',
        priority: 10
      }
    },
    {
      id: "top-customers",
      definition: {
        title: "Top Customers",
        component: (props) => (
          <TopCustomersCard {...props} />
        ),
        width: 4,
        height: 5,
        responsive: {
          md: { width: 6 },
          sm: { width: 6 },
          xs: { width: 4 }
        }
      },
      options: {
        category: 'customers',
        priority: 2
      }
    },
    {
      id: "customer-analytics",
      definition: {
        title: "Customer Analytics",
        component: (props) => (
          <LazyCard component={CustomerAnalyticsCard} {...props} />
        ),
        width: 6,
        height: 11,
        responsive: {
          md: { width: 8 },
          sm: { width: 6 },
          xs: { width: 4 }
        }
      },
      options: {
        category: 'customers',
        priority: 3
      }
    },
    {
      id: "orders-stats",
      definition: {
        title: "Orders Statistics",
        component: (props) => (
          <LazyCard component={lazy(() => import('../components/dashboard/cards/analytics/OrdersStatsCard'))} {...props} />
        ),
        width: 4,
        height: 11,
        responsive: {
          md: { width: 6 },
          sm: { width: 6 },
          xs: { width: 4 }
        }
      },
      options: {
        category: 'orders',
        priority: 1
      }
    },
    {
      id: "sales-overview",
      definition: {
        title: "Sales Overview",
        component: (props) => (
          <LazyCard component={SalesOverviewCard} {...props} />
        ),
        width: 8,
        height: 10,
        responsive: {
          md: { width: 8 },
          sm: { width: 6 },
          xs: { width: 4 }
        }
      },
      options: {
        category: 'sales',
        priority: 1
      }
    },
    {
      id: "cash-management-summary",
      definition: {
        title: "Cash Management",
        component: (props) => (
          <LazyCard component={CashManagementSummaryCard} {...props} />
        ),
        width: 4,
        height: 9,
        responsive: {
          md: { width: 6 },
          sm: { width: 6 },
          xs: { width: 4 }
        }
      },
      options: {
        category: 'cashmanagement',
        priority: 1
      }
    },
    {
      id: "cash-flow-forecast",
      definition: {
        title: "Cash Flow Forecast",
        component: (props) => (
          <LazyCard component={CashFlowForecastCard} {...props} />
        ),
        width: 4,
        height: 12,
        responsive: {
          md: { width: 6 },
          sm: { width: 6 },
          xs: { width: 4 }
        }
      },
      options: {
        category: 'cashmanagement',
        priority: 2
      }
    },
    {
      id: "receivables-summary",
      definition: {
        title: "Receivables",
        component: (props) => (
          <LazyCard component={ReceivablesCard} {...props} />
        ),
        width: 4,
        height: 7,
        responsive: {
          md: { width: 6 },
          sm: { width: 6 },
          xs: { width: 4 }
        }
      },
      options: {
        category: 'cashmanagement',
        priority: 3
      }
    },
    {
      id: "payables-summary",
      definition: {
        title: "Payables",
        component: (props) => (
          <LazyCard component={PayablesCard} {...props} />
        ),
        width: 4,
        height: 7,
        responsive: {
          md: { width: 6 },
          sm: { width: 6 },
          xs: { width: 4 }
        }
      },
      options: {
        category: 'cashmanagement',
        priority: 4
      }
    },
    {
      id: "stock-valuation",
      definition: {
        title: "Stock Valuation",
        component: (props) => (
          <LazyCard component={StockValuationCard} {...props} />
        ),
        width: 4,
        height: 9,
        responsive: {
          md: { width: 6 },
          sm: { width: 6 },
          xs: { width: 4 }
        }
      },
      options: {
        category: 'inventory',
        priority: 1
      }
    },
    {
      id: "purchases-by-supplier",
      definition: {
        title: "Purchases by Supplier",
        component: (props) => (
          <PurchasesBySupplierCard {...props} />
        ),
        width: 6,
        height: 10,
        responsive: {
          md: { width: 8 },
          sm: { width: 6 },
          xs: { width: 4 }
        }
      },
      options: {
        category: 'purchases',
        priority: 1
      }
    },
    {
      id: "customer-ageing",
      definition: {
        title: "Customer Outstandings",
        component: (props) => (
          <CustomerAgeingCard {...props} />
        ),
        width: 8,
        height: 15,
        responsive: {
          md: { width: 8 },
          sm: { width: 6 },
          xs: { width: 4 }
        }
      },
      options: {
        category: 'cashmanagement',
        priority: 2
      }
    }
  ];

  // Only register cards that the user has permission to view
  // For backwards compatibility and superuser access, register all cards if no specific permissions provided
  const cardsToRegister = (permittedCardIds && permittedCardIds.length > 0)
    ? cardDefinitions.filter(card => permittedCardIds.includes(card.id))
    : cardDefinitions;

  cardsToRegister.forEach(({ id, definition, options }) => {
    try {
      cardRegistry.registerCard({ id, ...definition }, options);
    } catch (error) {
      console.error(`[LazyCardDefinitions] Error registering card ${id}:`, error);
    }
  });

  // Register config-driven KPI cards
  const configCardsToRegister = (permittedCardIds && permittedCardIds.length > 0)
    ? dashboardCardDefinitions.filter(card => permittedCardIds.includes(card.id))
    : dashboardCardDefinitions;

  configCardsToRegister.forEach(cardConfig => {
    try {
      cardRegistry.registerCard({
        id: cardConfig.id,
        title: cardConfig.title,
        component: (props) => <UniversalDashboardCard config={cardConfig} {...props} />,
        width: cardConfig.width,
        height: cardConfig.height,
        minWidth: cardConfig.minWidth,
        minHeight: cardConfig.minHeight,
        responsive: cardConfig.responsive,
      }, {
        category: cardConfig.category || 'analytics',
        priority: cardConfig.priority || 50,
        roles: cardConfig.roles || [],
      });
    } catch (error) {
      console.error(`[LazyCardDefinitions] Error registering config card ${cardConfig.id}:`, error);
    }
  });
}

const registerUserCard = (cardConfig) => {
  try {
    if (cardRegistry.getCard(cardConfig.id)) return;
    cardRegistry.registerCard({
      id: cardConfig.id,
      title: cardConfig.title,
      component: (props) => <UniversalDashboardCard config={cardConfig} {...props} />,
      width: cardConfig.width || 4,
      height: cardConfig.height || 8,
      minWidth: cardConfig.minWidth || 3,
      minHeight: cardConfig.minHeight || 4,
      responsive: cardConfig.responsive || { sm: { width: 6 }, xs: { width: 4 } },
    }, {
      category: cardConfig.category || 'analytics',
      priority: cardConfig.priority || 50,
      roles: cardConfig.roles || [],
    });
  } catch (error) {
    console.error(`[LazyCardDefinitions] Error registering user card ${cardConfig.id}:`, error);
  }
};

export async function loadAndRegisterUserCards() {
  try {
    const userCards = await userCardService.load();
    userCards.forEach(registerUserCard);
    return userCards.length;
  } catch (error) {
    console.error('[LazyCardDefinitions] Error loading user cards:', error);
    return 0;
  }
}